import React, { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { TFunction, useTranslation } from 'react-i18next'
import { Pagination, TableProps } from 'antd'
import { TableCurrentDataSource } from 'antd/es/table/interface'
import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface'
import cl from 'classnames'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'

import { BasicTable, Spinner } from 'Shared/atoms'
import useSmallScreen from 'Shared/hooks/useSmallScreen'
import { convertToStringWithSeparator } from 'Shared/utils'

import { TableActions } from 'Core/interfaces'

import { filterSorterColumnsProps } from './utils'

import styles from './styles.module.scss'

//TODO created quickly to make independent component
export enum SortDirection {
	DESC = 'Descending',
	ASC = 'Ascending'
}

export interface PaginationParams {
	pageNumber?: number
	pageSize?: number
}

export interface SortingParams {
	field: string
	order: SortDirection
}

export interface CustomTableProps<T> extends TableProps<T> {
	dataSource: T[]
	defaultSorting?: {
		field: string
		order: SortDirection
	}
	filters?: { [key: string]: string }
	loading: boolean
	hasHorizontalScroll?: boolean
	getItems: (defaultSort?: SortingParams, actions?: TableActions) => void
	setPagination: (paginationParams: PaginationParams) => void
	setSorting?: (sortingParams: SortingParams | null) => void
	noDataParams?: { title?: string; description?: string }
	mobileWidth?: number
	toNotCallGetItems?: boolean
	onSort?: (order: SortDirection | undefined) => void
	isInitialLoadGetItems?: boolean
	isPaginationLoading?: boolean
	usePortalForPagination?: boolean
}

const getDefaultPagination = (t: TFunction<'translation'>): TablePaginationConfig => ({
	hideOnSinglePage: false,
	showSizeChanger: true,
	pageSizeOptions: ['20', '50', '100'],
	position: ['bottomCenter' as 'bottomCenter'],
	locale: { items_per_page: '' },
	showTitle: false,
	showLessItems: true,
	showTotal: (total: number, range: [number, number]) =>
		`${t('core:constants.SHOWING')} ${range[0]}—${range[1]} ${t(
			'core:constants.OF'
		)} ${convertToStringWithSeparator(total)}`
})

function Table<T extends {}>(props: CustomTableProps<T>) {
	const {
		mobileWidth = 1640,
		pagination,
		dataSource,
		getItems,
		setPagination,
		setSorting,
		className,
		defaultSorting,
		hasHorizontalScroll,
		rowClassName,
		noDataParams,
		columns,
		toNotCallGetItems,
		onSort,
		isInitialLoadGetItems = true,
		isPaginationLoading,
		usePortalForPagination = true
	} = props
	const isSmallScreen = useSmallScreen()
	const { t } = useTranslation()
	const tableContainerRef = useRef<HTMLDivElement | null>(null)
	const tableElement = document.getElementsByClassName('ant-table')?.[0] as HTMLElement
	const spinContainer = document.getElementsByClassName('ant-spin-container')?.[0] as HTMLElement
	const pagiationOptions = document.getElementsByClassName(
		'ant-pagination-options'
	)?.[0] as HTMLElement
	useEffect(() => {
		if (!document) {
			return
		}

		if (isInitialLoadGetItems) {
			getItems(defaultSorting)
		}
	}, [getItems, defaultSorting, isInitialLoadGetItems])

	useEffect(() => {
		if (pagiationOptions) {
			const startContainer = document.createElement('div')
			const endContainer = document.createElement('div')
			startContainer.textContent = t('core:constants.VIEW')
			endContainer.textContent = t('core:constants.ITEMS_PER_PAGE')
			pagiationOptions.insertBefore(startContainer, pagiationOptions.firstChild)
			pagiationOptions.appendChild(endContainer)
		}
	}, [pagiationOptions, t])

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<T> | SorterResult<T>[],
		extra: TableCurrentDataSource<T>
	) => {
		if (!isEmpty(sorter)) {
			const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter
			if (!sorterResult.order) {
				setSorting && setSorting(null)
			} else {
				// @ts-ignore
				const { sortDataIndex } = sorterResult.column
				// custom sort index instead of dataIndex
				const field = sortDataIndex
					? (sortDataIndex as string)
					: sorterResult.field!.toString()

				const order =
					sorterResult.order === 'ascend' ? SortDirection.ASC : SortDirection.DESC
				setSorting && setSorting({ field, order })
				onSort && onSort(order)
			}
		}

		if (tableElement) {
			tableElement.scrollTop = 0
		}

		if (extra.action === 'sort' && toNotCallGetItems) {
			return
		}

		getItems(undefined, { isSorting: extra.action === 'sort' })
	}

	const onPaginationChange = (page: number, pageSize: number) => {
		setPagination({ pageNumber: page, pageSize: pageSize })
		getItems(undefined, { isPagination: true })
	}

	useEffect(() => {
		// need this to fix ant bug with double scroll
		if (!!dataSource.length && hasHorizontalScroll) {
			tableElement?.scrollBy(0, 1)
		}
	}, [dataSource, tableElement, hasHorizontalScroll])

	const formatColumns = useMemo(
		() => (isSmallScreen && columns ? filterSorterColumnsProps(columns) : columns),
		[columns, isSmallScreen]
	)

	const scrollData = isSmallScreen
		? { x: mobileWidth }
		: hasHorizontalScroll
		? { x: 1640 }
		: undefined
	const DEFAULT_PAGINATION = getDefaultPagination(t)
	const paginationElement = (
		<div className={styles.paginationWrapper}>
			{isPaginationLoading ? (
				<Spinner />
			) : (
				<Pagination
					className={styles.pagination}
					{...pagination}
					{...DEFAULT_PAGINATION}
					onChange={onPaginationChange}
					showTotal={isSmallScreen ? undefined : DEFAULT_PAGINATION.showTotal}
				/>
			)}
		</div>
	)

	return (
		<div ref={tableContainerRef} className={styles.tableContainer}>
			<BasicTable
				scroll={scrollData}
				{...props}
				columns={formatColumns}
				dataSource={dataSource}
				pagination={false}
				showSorterTooltip={!isSmallScreen}
				locale={{
					triggerAsc: t('core:constants.CLICK_TO_SORT_ASCENDING'),
					triggerDesc: t('core:constants.CLICK_TO_SORT_DESCENDING'),
					cancelSort: t('core:constants.CLICK_TO_CANCEL_SORTING')
				}}
				noDataParams={noDataParams}
				onChange={handleTableChange}
				className={cl(
					styles.tableWrapper,
					className,
					!dataSource.length && styles.emptyTable
				)}
				rowClassName={(record: T, index: number, indent: number) =>
					cl(
						styles.editableRow,
						isString(rowClassName)
							? rowClassName
							: rowClassName && rowClassName(record, index, indent)
					)
				}
				{...(tableContainerRef &&
					dataSource.length > 0 && {
						sticky: {
							offsetHeader: -16,
							offsetScroll: 0,
							getContainer: () => tableElement
						}
					})}
			/>
			{usePortalForPagination && spinContainer
				? createPortal(paginationElement, spinContainer)
				: paginationElement}
		</div>
	)
}

export default Table
